import { Account, AccountType } from '@ndvr/portfolio-api';

import { BaseCard } from '@/components/BaseCard';
import { EditableCardGroupProvider } from '@/components/EditableCardGroupContext';
import { useRequiredParams } from 'utils';
import { useHouseholdRoutePaths } from 'utils/hooks/useHouseholdRoutePaths';
import { useAllocation } from 'views/portfolio-engine/allocation-builder/allocations/allocation-queries';
import { StrategyAllocations } from 'views/portfolio-engine/allocation-builder/allocations/StrategyAllocations';
import { useAccountGroup } from 'views/portfolio/account-group-queries';

import { AccountAllocationCard } from './account-overview/AccountAllocationCard';
import { GeneralCard } from './account-overview/GeneralCard';
import { TaxBudgetCard } from './account-overview/TaxBudgetCard';

export const ACCOUNT_OVERVIEW_EDIT_GROUP = 'account-overview';

export const AccountOverview = ({ account }: { account: Account }) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();
    const { data: accountGroup, isLoading: isLoadingAccountGroup } = useAccountGroup(account.accountGroupGUID);
    const { data: portfolioAllocation } = useAllocation(accountGroup?.allocationGUID);

    const isPortfolioLevel = !!accountGroup?.allocationGUID;
    const targetAllocationPath = `${PortfolioPageRoutePath(globalAccountGUID, account.accountGroupGUID)}/target-allocation`;

    return (
        <EditableCardGroupProvider>
            <div className="grid grid-cols-2 items-start gap-6 py-2">
                <GeneralCard account={account} editGroup={ACCOUNT_OVERVIEW_EDIT_GROUP} />

                <div className="space-y-6">
                    {account.accountType === AccountType.TAXABLE && (
                        <TaxBudgetCard account={account} editGroup={ACCOUNT_OVERVIEW_EDIT_GROUP} />
                    )}
                    {isLoadingAccountGroup ? null : isPortfolioLevel ? (
                        <BaseCard
                            headerClassName="pt-3 pb-3"
                            title="Portfolio-level allocation"
                            titleLink={targetAllocationPath}
                        >
                            {portfolioAllocation ? (
                                <div className="space-y-2">
                                    <div className="rounded-md border border-gray-300 bg-gray-50 p-3">
                                        {portfolioAllocation.allocationName}
                                    </div>
                                    <StrategyAllocations
                                        strategyAllocations={portfolioAllocation.strategyAllocations}
                                    />
                                </div>
                            ) : (
                                <div className="text-muted-foreground text-center">Loading…</div>
                            )}
                        </BaseCard>
                    ) : (
                        <AccountAllocationCard account={account} editGroup={ACCOUNT_OVERVIEW_EDIT_GROUP} />
                    )}
                </div>
            </div>
        </EditableCardGroupProvider>
    );
};
